import { cn } from '@/lib/utils';
import { Bell } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useNotifications } from '@/hooks/useNotifications';
import { useLeagueChat } from '@/hooks/useLeagueChat';

export type Tab = 'dashboard' | 'schedule' | 'leaderboard' | 'players' | 'matchups' | 'rosters' | 'myteam' | 'standings' | 'fantasy-schedule' | 'draft' | 'sabotage' | 'settings' | 'transactions';

interface MLBBetaNavProps {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  isCommissioner?: boolean;
  onOpenNotifications?: () => void;
  draftRoomUrl?: string;
}

const tabs: { id: Tab; label: string }[] = [
  { id: 'dashboard', label: 'Home' },
  { id: 'myteam', label: '🏟️ My Team' },
  { id: 'matchups', label: '⚔️ Matchups' },
  { id: 'standings', label: '🏆 Standings' },
  { id: 'fantasy-schedule', label: '📅 Schedule' },
  { id: 'draft', label: '📝 Draft' },
  { id: 'players', label: '👤 Players' },
  { id: 'sabotage', label: '💣 Sabotage' },
];

const MLBBetaNav = ({ activeTab, onTabChange, onOpenNotifications, draftRoomUrl }: MLBBetaNavProps) => {
  const { unreadCount } = useNotifications();
  const { totalUnread: chatUnread } = useLeagueChat();
  const totalBadge = unreadCount + chatUnread;

  return (
    <div className="hidden sm:flex items-center gap-0.5">
      {tabs.map((tab) => {
        const cls = cn(
          'px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap',
          activeTab === tab.id
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
        );
        // Draft tab on desktop: render as native <a> so Chrome never blocks it
        if (tab.id === 'draft' && draftRoomUrl) {
          return (
            <a key={tab.id} href={draftRoomUrl} target="_blank" rel="noopener noreferrer" className={cls}>
              {tab.label}
            </a>
          );
        }
        return (
          <button key={tab.id} onClick={() => onTabChange(tab.id)} className={cls}>
            {tab.label}
          </button>
        );
      })}
      {/* Notification bell inline in nav */}
      <button
        onClick={onOpenNotifications}
        className="relative px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap text-muted-foreground hover:text-foreground hover:bg-muted"
      >
        <Bell className="h-4 w-4" />
        {totalBadge > 0 && (
          <Badge className="absolute -top-1 -right-0.5 h-4 w-4 flex items-center justify-center p-0 text-[9px] bg-damage border-0">
            {totalBadge > 9 ? '9+' : totalBadge}
          </Badge>
        )}
      </button>
    </div>
  );
};

export default MLBBetaNav;
